// AICA Card Component
// Bloomberg Terminal style: Subtle elevation on hover, precise animations
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  hoverable?: boolean; // Enable hover effects
  'data-testid'?: string; // E2E testing ID
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    { className, hoverable, onClick, role, children, 'data-testid': testId, ...props },
    ref
  ) => {
    // Auto-detect if card is interactive (has onClick or role="button")
    const isInteractive = onClick !== undefined || role === 'button';
    // Enable hover by default for interactive cards, or when explicitly set
    const shouldHover = hoverable !== undefined ? hoverable : isInteractive;

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!shouldHover) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      e.currentTarget.style.setProperty('--mouse-x', `${x}px`);
      e.currentTarget.style.setProperty('--mouse-y', `${y}px`);
    };

    return (
      <div
        ref={ref}
        onClick={onClick}
        role={isInteractive && !role ? 'button' : role}
        tabIndex={isInteractive ? 0 : undefined}
        onKeyDown={
          isInteractive && onClick
            ? (e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.currentTarget.click();
                }
              }
            : undefined
        }
        data-testid={testId}
        onMouseMove={handleMouseMove}
        className={cn(
          'group relative rounded-card bg-bg-surface border border-bg-elevated overflow-hidden',
          'p-4',
          // Meridian Dark: hover matches prototype — border change only, no glow
          shouldHover &&
            'transition-all duration-150 hover:bg-bg-elevated hover:border-bg-muted cursor-pointer',
          // Reduced motion support
          'motion-reduce:transition-none',
          className
        )}
        {...props}
      >
        {/* Spotlight Overlay */}
        {shouldHover && (
          <div className="pointer-events-none absolute -inset-px opacity-0 transition duration-150 group-hover:opacity-100 spotlight-gradient" />
        )}
        {/* Content wrapper to ensure text sits above spotlight */}
        <div className="relative h-full">{children}</div>
      </div>
    );
  }
);

Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, CardProps>(
  ({ className, 'data-testid': testId, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-testid={testId}
        className={cn('flex flex-col space-y-1.5 pb-4', className)}
        {...props}
      />
    );
  }
);

CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement> & {
    'data-testid'?: string;
    'aria-label'?: string;
  }
>(
  (
    { className, 'data-testid': testId, children, 'aria-label': ariaLabel, ...props },
    ref
  ) => {
    return (
      <h3
        ref={ref}
        data-testid={testId}
        aria-label={!children && !ariaLabel ? 'Card' : ariaLabel}
        className={cn('text-sm font-medium leading-none tracking-tight', className)}
        {...props}
      >
        {children}
      </h3>
    );
  }
);

CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement> & { 'data-testid'?: string }
>(({ className, 'data-testid': testId, ...props }, ref) => {
  return (
    <p
      ref={ref}
      data-testid={testId}
      className={cn('text-sm text-fg-muted', className)}
      {...props}
    />
  );
});

CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<HTMLDivElement, CardProps>(
  ({ className, 'data-testid': testId, ...props }, ref) => {
    return (
      <div ref={ref} data-testid={testId} className={cn('pt-0', className)} {...props} />
    );
  }
);

CardContent.displayName = 'CardContent';
